<template>
  <div class="message"
       :title="datetime"
       :class="{patient: fromPatient}"
       ref="messageEduc">

    <div v-if="!content || loading" class="spinner-container">
      <smd-loading-spinner></smd-loading-spinner>
      <p v-if="error" class="Body4 text-center">{{error}}</p>
      <smd-btn v-if="error"
               section="4"
               variant="blue-enabled"
               @click="tryAgain">
        Try again
      </smd-btn>
    </div>


    <div class="educ-content" v-if="content">
      <div>
        <p class="Body2">{{ content.title }}</p>
        <p :class="{'m-b-10' : content.description}"
           class="Body4">By {{ content.author}}</p>
        <p class="Body1">{{ content.description }}</p>
      </div>

      <div class="educ-content__content">
        <div v-if="content.coverurl"
            class="cover-img-container">
          <img :src="content.coverurl" :alt="content.title" @load="contentLoaded"/>
        </div>

        <p v-if="filesCount"
           class="Body2 files"
           :class="{'m-b-10' : content.pxmaterialtype !== 'custom'}"
           @click="clickAttachedFile">
          <span class="ns-icon-attachment"></span>
          {{filesCount}} attached {{formattedFileWord}}
        </p>
      </div>

      <smd-btn v-if="content.pxmaterialtype === 'custom'"
               class="educ-content__btn"
               section="4"
               variant="blue-enabled"
               @click="viewContent">
        Learn More
      </smd-btn>
    </div>

    <p class="Label1 gray-400">{{ formattedTime }}</p>
  </div>

</template>

<script>
  import {EducationService} from '@/services/education';
  import MixinPreviewEducFile from '@/mixins/preview-educ-file-mixin';
  import MsgInitialVisibleChecker from '@/mixins/msg-initial-visible-checker-mixin';
  import {toPhilMoment} from '@/utils/dates';

  import educContentPage from '@/components/messages/educ-content-page';

  export default {
    name: "MessageEduc",
    mixins: [
      MixinPreviewEducFile,
      MsgInitialVisibleChecker,
    ],
    props: {
      message: {type: Object, required: true},
      token: {type: String, required: true},
      fromPatient: {type: Boolean, required: true},
    },
    data() {
      return {
        content: null,
        error: null,
        loading: false,
      }
    },
    computed: {
      formattedTime() {
        return toPhilMoment(this.message.received).format('hh:mm a');
      },
      datetime() {
        return toPhilMoment(this.message.received).format('MMMM D, YYYY hh:mm a');
      },
      filesCount() {
        return this.content.files ? this.content.files.length : null;
      },
      formattedFileWord() {
        return this.filesCount > 1 ? 'files' : 'file';
      },
    },
    mounted() {
      this.observeMessageEducIsVisible();
      this.observeMsgEl(this.$refs.messageEduc);
    },
    methods: {
      observeMessageEducIsVisible() {
        const options = {
          root: null,
          threshold: 0,
        }

        const loadMessageEduc = ([entry], observer) => {
          if (entry && entry.isIntersecting) {
            this.getEducContent();
            observer.disconnect();
          }
        }

        const loadMessageEducObserver = new IntersectionObserver(loadMessageEduc, options);
        loadMessageEducObserver.observe(this.$refs.messageEduc);
      },
      async getEducContent() {
        try {
          const {data} = await EducationService.getSpecificPxMaterial(this.message.msgid, this.token);
          if (!data) throw Error();
          this.content = data;
          this.contentLoaded();
        } catch (error) {
          console.error(error);
          this.loading = true;
          this.error = "An unexpected error occured while loading this message."
        }
      },
       viewContent() {
        this.$modal.open({
          component: educContentPage,
          size: 'full-screen',
          position: 'detached auto-height',
          props: {
            content: this.content,
            token: this.token,
            metadata: this.message.metadata,
          }
        });
      },
      clickAttachedFile() {
        this.getFilesAttached(this.content.files,
                              this.content.msgid,
                              this.token);
      },
      tryAgain() {
        this.error = null;
        this.getEducContent();
      },
      contentLoaded() {
        if (this.isMsgInitialVisible) {
          this.$emit('loaded');
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  .spinner-container {
    @extend %layout--flex-center-xy;
    height: 200px;
    width: 200px;
  }

  .message {
    position: relative;
    display: block;
    overflow: hidden;
    border-radius: radius(medium);
    padding: space(s) space(m);
    background-color: color(gray-50);

    .time {
      font-size: font(s);
    }
  }

  .patient {
    background-color: color(blue, brand);

    p {
      color: color(white, text) !important;
      word-break: break-word;
    }

    .time {
      text-align: right;
    }
  }

  .educ-content {
    @include flex-col();
    gap: 10px;

    &__content {
      @include flex-col();
      @extend %align-items-flex-start;

      .files {
        @extend %layout--flex-row-center-x;
        gap: space(xs);
        margin-top: 6px;
        cursor: pointer;
      }
    }

    &__btn {
      width: 186.27px;
      align-self: center;
      margin-top: space(xs);
    }
  }

  .cover-img-container {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
</style>
